import '../styles/LoginVerificationPanel.css';
import React, { useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function RuleMark() {
  return (
    <svg viewBox="0 0 44 12" fill="none" aria-hidden="true">
      <path d="M1 8.5C8 7.2 14 9.2 21 6.6c5-1.9 9-3.9 22-4.6" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
      <path d="M1 11c9-1 17 0 26-1.8" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" opacity="0.5" />
    </svg>
  );
}

function Field({ id, label, type, value, onChange, autoComplete, placeholder, error, describedBy }) {
  return (
    <div className={`lvp-field${error ? ' lvp-field--invalid' : ''}`}>
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        name={id}
        className="lvp-input"
        type={type}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? describedBy : undefined}
        spellCheck={type === 'email' ? false : undefined}
      />
      {error ? (
        <p className="lvp-field-error" id={describedBy}>{error}</p>
      ) : null}
    </div>
  );
}

function LoginVerificationPanel({ onLogin }) {
  const { data: researchers } = useProjectCollection('researchers');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const errors = {};
    if (!email.trim()) errors.email = 'Enter the email you enrolled with.';
    else if (!EMAIL_PATTERN.test(email.trim())) errors.email = 'Enter a complete email address.';
    if (!password) errors.password = 'Enter your password.';

    setFieldErrors(errors);
    setFormError('');
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      let result;
      if (typeof onLogin === 'function') {
        result = await onLogin({ email: email.trim(), password });
      } else {
        const record = (researchers || []).find(
          (r) => String(r.email || '').trim().toLowerCase() === email.trim().toLowerCase()
        );
        result = record && record.password === password
          ? { ok: true }
          : { ok: false, error: 'We could not verify those credentials.' };
      }
      if (result && result.ok) {
        window.location.assign('/crawls');
        return;
      }
      setFormError((result && result.error) || 'We could not verify those credentials. Please try again.');
    } catch (err) {
      setFormError('The session could not be verified just now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="lvp-root" aria-labelledby="lvp-heading">
      <div className="lvp-rail" data-content-rail="primary">
        <div className="lvp-panel">
          <p className="lvp-opener">
            <RuleMark />
            <span className="lvp-label">Returning researcher</span>
          </p>
          <h2 className="lvp-heading" id="lvp-heading">Verify your identity</h2>
          <p className="lvp-lede">
            Sign in to open the crawl history and extracted leads saved to your account.
          </p>

          <form className="lvp-form" onSubmit={handleSubmit} noValidate>
            <div className="lvp-credentials">
              <Field
                id="lvp-email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="you@example.com"
                error={fieldErrors.email}
                describedBy="lvp-email-error"
              />
              <Field
                id="lvp-password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="Your password"
                error={fieldErrors.password}
                describedBy="lvp-password-error"
              />
            </div>

            <div className="lvp-error-region">
              {formError ? (
                <div className="lvp-error" role="alert" aria-live="assertive">
                  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.2" />
                    <path d="M10 6v5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    <circle cx="10" cy="13.8" r="0.9" fill="currentColor" />
                  </svg>
                  <p>{formError}</p>
                </div>
              ) : null}
            </div>

            <button
              type="submit"
              className="lvp-submit"
              disabled={submitting}
              aria-busy={submitting ? 'true' : undefined}
            >
              {submitting ? (
                <>
                  <span className="lvp-spinner" aria-hidden="true" />
                  <span>Verifying…</span>
                </>
              ) : (
                <span>Verify and continue</span>
              )}
            </button>
          </form>

          <p className="lvp-footnote">
            Not enrolled yet?
            <a className="lvp-signup" href="/sign-up">Sign Up</a>
          </p>
        </div>
      </div>
    </section>
  );
}

export default LoginVerificationPanel;